import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import enTranslations from '../assets/i18n/en.json';
import esTranslations from '../assets/i18n/es.json';
import hiTranslations from '../assets/i18n/hi.json';
import ptBRTranslations from '../assets/i18n/pt-BR.json';
import ruTranslations from '../assets/i18n/ru.json';
import trTranslations from '../assets/i18n/tr.json';
import viTranslations from '../assets/i18n/vi.json';
import zhHansTranslations from '../assets/i18n/zh-Hans.json';
import zhHantTranslations from '../assets/i18n/zh-Hant.json';
import { getJwtLanguageFromAccessToken } from './auth.ts';
import { ENV_CONSTS } from './env-consts.ts';
import { getLocalStorageValue } from './local-storage.ts';

const siteType = ENV_CONSTS.SITE_TYPE as 'b2b' | 'b2c';

export const supportedLanguages = [
  'en',
  'es',
  'pt-BR',
  'zh-Hant',
  'zh-Hans',
  'ru',
  'tr',
  'vi',
  'hi',
] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number];

type TranslationCatalog = typeof enTranslations;

const catalogs: Record<SupportedLanguage, TranslationCatalog> = {
  en: enTranslations,
  es: esTranslations,
  'pt-BR': ptBRTranslations,
  'zh-Hant': zhHantTranslations,
  'zh-Hans': zhHansTranslations,
  ru: ruTranslations,
  tr: trTranslations,
  vi: viTranslations,
  hi: hiTranslations,
};

const LANGUAGE_ALIASES: Record<string, SupportedLanguage> = {
  en: 'en',
  eng: 'en',
  es: 'es',
  spa: 'es',
  pt: 'pt-BR',
  'pt-br': 'pt-BR',
  br: 'pt-BR',
  por: 'pt-BR',
  'zh-hant': 'zh-Hant',
  'zh-tw': 'zh-Hant',
  'zh-hk': 'zh-Hant',
  'zh-mo': 'zh-Hant',
  tw: 'zh-Hant',
  cht: 'zh-Hant',
  'zh-hans': 'zh-Hans',
  'zh-cn': 'zh-Hans',
  'zh-sg': 'zh-Hans',
  zh: 'zh-Hans',
  cn: 'zh-Hans',
  chs: 'zh-Hans',
  ru: 'ru',
  rus: 'ru',
  tr: 'tr',
  tur: 'tr',
  vi: 'vi',
  vn: 'vi',
  vie: 'vi',
  hi: 'hi',
  hin: 'hi',
};

const buildCatalog = (translations: TranslationCatalog) => ({
  translation: {
    ...translations.common,
    ...translations[siteType],
  },
});

export const resources = {
  en: buildCatalog(catalogs.en),
  es: buildCatalog(catalogs.es),
  'pt-BR': buildCatalog(catalogs['pt-BR']),
  'zh-Hant': buildCatalog(catalogs['zh-Hant']),
  'zh-Hans': buildCatalog(catalogs['zh-Hans']),
  ru: buildCatalog(catalogs.ru),
  tr: buildCatalog(catalogs.tr),
  vi: buildCatalog(catalogs.vi),
  hi: buildCatalog(catalogs.hi),
} as const;

export type TranslationKey = keyof (typeof resources)['en']['translation'];

export const resolveSupportedLanguage = (
  language?: string | null
): SupportedLanguage => {
  if (!language) return 'en';

  const normalized = language.trim().replace(/_/g, '-').toLowerCase();
  if (LANGUAGE_ALIASES[normalized]) {
    return LANGUAGE_ALIASES[normalized];
  }

  const prefix = normalized.split('-')[0];
  return LANGUAGE_ALIASES[prefix] ?? 'en';
};

export const getLanguageFromUrl = (
  search: string = typeof window === 'undefined' ? '' : window.location.search
): string | null => {
  const language = new URLSearchParams(search).get('language')?.trim();
  return language || null;
};

export const applyLanguageFromApi = (language?: string | null) => {
  const resolved = resolveSupportedLanguage(language);
  if (i18n.language !== resolved) {
    void i18n.changeLanguage(resolved);
  }
};

const getInitialLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') return 'en';

  const urlLanguage = getLanguageFromUrl();
  if (urlLanguage) {
    return resolveSupportedLanguage(urlLanguage);
  }

  const accessToken = getLocalStorageValue('userAccessToken')?.accessToken;
  return resolveSupportedLanguage(getJwtLanguageFromAccessToken(accessToken));
};

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: 'en',
  supportedLngs: [...supportedLanguages],
  load: 'currentOnly',
  interpolation: {
    escapeValue: false,
  },
});

// noinspection JSUnusedGlobalSymbols
export default i18n;
